import { upsertLocally } from '@paragon/client-tools';
import { CeilingPlane, PlaneCut } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { CeilingPlaneEditInstructions } from '@paragon/layout';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray } from '@paragon/utilities';

import { getActiveLayoutLevelGuidOrNull, getAllCeilingPlanesDictionary } from '../../selectors/layoutDataSelectors';
import { getPickingObjectInfo } from '../../selectors/pickingSelectors';
import {
  applicationOnInsertingCeilingPlane,
  applicationOnPickingPlane,
  clearPickingObject,
  isInsertionCeilingPlaneFullyDefined,
} from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { selectItem } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import { runPostCutCreationHooks } from './roofPlaneActions';

export const startInsertingCeilingPlane =
  (options?: { levelGuid?: string }): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const levelGuid = options?.levelGuid ?? getActiveLayoutLevelGuidOrNull(state) ?? null;

    if (levelGuid) dispatch(ensureLevelIsVisible(levelGuid, { exclusive: true }));

    dispatch(
      applicationOnInsertingCeilingPlane({
        ...state.layout.ceilingPlaneInsertionSettings.defaultProperties,
        levelGuid,
      }),
    );
  };

export const finishInsertingCeilingPlane =
  (options?: { postInsertionAction?: 'repeat' }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const { insertingObjectInfo } = state.application;
    const { ceilingPlaneInsertionSettings } = state.layout;

    if (insertingObjectInfo?.type !== 'ceilingPlane') return;

    if (!isInsertionCeilingPlaneFullyDefined(insertingObjectInfo.data)) return;

    const ceilingPlane = insertingObjectInfo.data;
    const postInsertionAction = options?.postInsertionAction ?? 'finish';
    dispatch(
      layoutSlice.actions.ceilingPlaneCreated({ ceilingPlane, postInsertionAction, ceilingPlaneInsertionSettings }),
    );

    await dispatch(saveCeilingPlanes());

    if (options?.postInsertionAction == null) {
      dispatch(selectItem(ceilingPlane, 'ceilingPlane'));
    }
  };

export const beginCuttingCeilingPlane =
  (guid: string): AsyncThunkActionVoid =>
  async (dispatch) => {
    await dispatch(clearCeilingPlaneCuts(guid));
    dispatch(applicationOnPickingPlane({ type: 'ceiling-cut', planeGuidToCut: guid }));
  };

export const handleCeilingPlaneCutPicked =
  (cut: PlaneCut): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const pickingObjectInfo = getPickingObjectInfo(state);

    if (pickingObjectInfo?.type !== 'pickingPlane' || pickingObjectInfo.targetInfo.type !== 'ceiling-cut') {
      throw new Error('Invalid picking state.');
    }

    if (cut.type === 'AgainstPlane' && cut.cuttingPlaneGuid === pickingObjectInfo.targetInfo.planeGuidToCut) {
      dispatch(enqueueWarning('Cannot cut a ceiling plane against itself.'));
      return;
    }

    const ceilingPlaneToCut = state.layout.ceilingPlanes[pickingObjectInfo.targetInfo.planeGuidToCut];
    if (ceilingPlaneToCut == null) throw new Error('Unable to find roof plane to cut.');

    await dispatch(
      editCeilingPlanes({
        guid: ceilingPlaneToCut.guid,
        editedProperties: {
          cuts: ceilingPlaneToCut.cuts.concat(cut),
        },
      }),
    );

    // FIXME: it would be awesome to have a heuristic to exit picking mode automatically once a plane is fully
    //   bounded by cuts. So far we haven't found a good one.

    runPostCutCreationHooks();
  };

export const finishCuttingCeilingPlane = () => clearPickingObject();

const ERROR_SAVING_MESSAGE = 'Unable to save ceiling plane. Please try again or contact Paragon Customer Support.';

export const editCeilingPlanes =
  (
    instructions: CeilingPlaneEditInstructions | CeilingPlaneEditInstructions[],
    options?: { createdPlanes?: CeilingPlane[] },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    let existingPlanes = getAllCeilingPlanesDictionary(state);
    if (options?.createdPlanes) {
      existingPlanes = upsertLocally(existingPlanes, options.createdPlanes);
    }

    const editedPlanes = forceArray(instructions).map(({ guid, editedProperties }) => {
      const ceilingPlane = existingPlanes[guid];
      if (ceilingPlane == null) {
        dispatch(enqueueError(ERROR_SAVING_MESSAGE));
        throw new Error(`Unable to find ceiling plane with guid ${guid} to edit.`);
      }

      return { ...ceilingPlane, ...editedProperties };
    });

    dispatch(layoutSlice.actions.ceilingPlanesUpserted(editedPlanes));

    if (state.application.openContext?.type === 'project') {
      await dispatch(saveCeilingPlanes());
    }
  };

export const clearCeilingPlaneCuts = (guid: string) => editCeilingPlanes({ guid, editedProperties: { cuts: [] } });

export const saveCeilingPlanes = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return;

  const spinnerId = dispatch(addSpinnerMessage('Saving ceiling planes...'));

  try {
    await designService.projects.replaceCeilingPlanes(
      state.application.openContext.projectGuid,
      state.layout.ceilingPlanes,
    );
  } catch (error) {
    console.error(error);
    dispatch(enqueueError(ERROR_SAVING_MESSAGE));
    return;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};
